import { useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { tokenRequest } from '@/config/msalConfig';

/**
 * Hook to handle admin consent for Deep Analysis.
 *
 * Deep Analysis uses application permissions (client credentials) on the backend,
 * but the customer's tenant admin must grant admin consent first. This hook:
 *
 * 1. Attempts to acquire an API token (same as regular Discovery)
 * 2. The backend checks whether app permissions are consented by attempting
 *    the Graph calls — if they fail with Authorization_RequestDenied,
 *    it returns an error and the frontend shows the admin consent button
 *
 * Admin consent URL format:
 * https://login.microsoftonline.com/{tenant_id}/adminconsent
 *   ?client_id={app_id}
 *   &redirect_uri={redirect}
 */
export function useAdminConsent() {
  const { instance } = useMsal();
  const [isAcquiring, setIsAcquiring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Acquire an API token for authenticated backend calls.
   */
  const acquireApiToken = useCallback(async (): Promise<string | null> => {
    setIsAcquiring(true);
    setError(null);

    const activeAccount = instance.getActiveAccount();
    if (!activeAccount) {
      setError('No active account. Please sign in first.');
      setIsAcquiring(false);
      return null;
    }

    try {
      // Acquire API-scoped token
      try {
        const response = await instance.acquireTokenSilent({
          ...tokenRequest,
          account: activeAccount,
        });
        setIsAcquiring(false);
        return response.accessToken;
      } catch (silentError) {
        if (silentError instanceof InteractionRequiredAuthError) {
          const response = await instance.acquireTokenPopup({
            ...tokenRequest,
            account: activeAccount,
          });
          setIsAcquiring(false);
          return response.accessToken;
        }
        throw silentError;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Token acquisition failed';
      setError(msg);
      setIsAcquiring(false);
      return null;
    }
  }, [instance]);

  /**
   * Open the admin consent URL in a popup.
   * After consent is granted, the popup redirects back and the user can retry.
   */
  const triggerAdminConsent = useCallback(() => {
    const clientId = import.meta.env.VITE_AZURE_AD_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/consent-callback`;

    const activeAccount = instance.getActiveAccount();
    const tenantId = activeAccount?.tenantId || 'common';

    const consentUrl =
      `https://login.microsoftonline.com/${tenantId}/adminconsent` +
      `?client_id=${clientId}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}`;

    // Open in popup — consent-callback route will self-close
    const popup = window.open(consentUrl, 'adminConsent', 'width=600,height=800');
    if (popup) {
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          // Popup closed after consent — refresh so the page picks up new permissions
          window.location.reload();
        }
      }, 500);
    }
  }, [instance]);

  return {
    acquireApiToken,
    triggerAdminConsent,
    isAcquiring,
    error,
  };
}
